/** /ext_ids/ — restech external-ID mappings across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — see ConsoleExtIdFilterNode in the
 * OpenAPI spec.
 *
 * Rows come from the `demo_ext_id_index` view, which unions the four places an
 * external ID can live: Group_Tour.custom_id and Itinerary_Group /
 * Activity.custom_id (kind `primary`) plus Additional_Ext_ID (kind `alias`).
 * Row ids are therefore composite strings — `aeid:1234`, `tour:56` — never
 * integers. Do not Number() them.
 *
 * Mutations resolve the composite id back to the model that owns the data, so
 * validation, audit and change history stay on the real rows. The two kinds are
 * NOT symmetric: an alias (Additional_Ext_ID) is fully editable, while a
 * primary is an entity's own custom_id column with no companion fields — only
 * its value can change, and it cannot be deleted from here at all (that would
 * mean blanking a column on a live tour).
 */
import { apiDelete, apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export type ExtIdKind = 'primary' | 'alias';
export type ExtIdModel = 'tour' | 'itinerary' | 'activity';

/** The eight Additional_Ext_ID.IDType values. Null on primary rows —
 *  Group_Tour.custom_id has no companion columns. */
export type ExtIdType =
  | 'product_id'
  | 'package_id'
  | 'option_id'
  | 'product_with_departure_time'
  | 'product_with_departure_weekday'
  | 'product_with_departure_weekday_and_time'
  | 'rate_id'
  | 'other';

export const EXT_ID_TYPES: { value: ExtIdType; label: string }[] = [
  { value: 'product_id', label: 'Product ID' },
  { value: 'package_id', label: 'Package ID' },
  { value: 'option_id', label: 'Option ID (Ventrata)' },
  { value: 'product_with_departure_time', label: 'Product ID + departure time' },
  { value: 'product_with_departure_weekday', label: 'Product ID + departure weekday' },
  {
    value: 'product_with_departure_weekday_and_time',
    label: 'Product ID + departure weekday and time',
  },
  { value: 'rate_id', label: 'Product ID + rate (Bokun)' },
  { value: 'other', label: 'Other / templated' },
];

/** ConsoleTenantRef — the tenant reference every console list serialises. */
export interface ExtIdTenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

/**
 * One pipeline's verdict on one ID.
 *
 * `na` is NOT a failure — it means the tenant does not run that pipeline at
 * all. A direct-XML operator has no booking integration, so its compound IDs
 * (`35/6415/EN`) are correct rather than broken. Only `no` is a finding, and
 * the UI must render the three states distinctly or 1728 production rows read
 * as errors.
 */
export type ExtIdFacetState = 'yes' | 'no' | 'na';

export interface ExtIdFacet {
  state: ExtIdFacetState;
  reason: string;
  detail: string | null;
}

/** The three consumers, resolved independently. No overall verdict by design. */
export interface ExtIdUsedBy {
  import: ExtIdFacet;
  bookings: ExtIdFacet;
  api: ExtIdFacet;
}

export interface ExtIdTarget {
  model: ExtIdModel;
  id: number;
  /** null on a dangling alias — nothing cascades a GenericForeignKey. */
  name: string | null;
  is_deleted: boolean;
}

export interface ExtIdListItem {
  /** Composite: `<tour|aeid|itg|act>:<pk>`. */
  id: string;
  kind: ExtIdKind;
  model: ExtIdModel;
  object_id: number;
  tenant: ExtIdTenantRef;
  /** Exactly as stored — whitespace intact. This is what the matcher compares. */
  value: string;
  /** Stripped + lowercased; what search matches against. */
  value_norm: string;
  target: ExtIdTarget;
  id_type: ExtIdType | null;
  use_for_matching: boolean;
  api_queryable: boolean;
  /** Ops-convenience scoping. Consulted for API interaction, NOT for matching. */
  integration: string | null;
  restech_account: string | null;
  /** Free-text ops note: what this mapping is for, why it is parked. Alias
   *  rows only — a primary ID has no companion columns. */
  comment: string | null;
  /** The product in the restech, or the tour on the operator's site. Alias only. */
  ext_link: string | null;
  /**
   * Import-owned alias: an import created it from `<ext_ids>` and keeps it in step
   * on every sync (`integration` names the restech; null = a direct XML import).
   * The server refuses any edit but `managed` itself, and a delete, until ops take
   * it over (`managed: false`). Always false on primary rows.
   */
  managed: boolean;
  /** false => get_tour_map cannot see this primary row. */
  has_ext_id: boolean;
  is_deleted: boolean;
  is_dangling: boolean;
  /**
   * The ops naming convention for a parked mapping (`OFF`, `test`), when
   * present. Display only — the platform never rewrites the value.
   */
  parked_marker: string | null;
  used_by: ExtIdUsedBy;
  created_ts: string | null;
}

export interface ExtIdSibling {
  id: string;
  kind: ExtIdKind;
  model: ExtIdModel;
  value: string;
  use_for_matching: boolean;
  target: { id: number; name: string | null };
}

export interface ExtIdTag {
  id: number;
  name: string;
  category: string | null;
}

export interface ExtIdDetail extends ExtIdListItem {
  /** Other rows in this tenant carrying the same normalised value. */
  siblings: ExtIdSibling[];
  /** Tags applied to a participant when a booking matches through this alias. */
  tags: ExtIdTag[];
  /** Additional tours a matching booking is also added to (dependent bookings). */
  fanout_targets: { id: number; name: string }[];
}

export interface ExtIdListParams {
  page?: number;
  page_size?: number;
  /** id|tenant|value|kind|model|target|id_type|matching|queryable|integration|
   *  account|created_ts ('-' desc). Default value. */
  ordering?: string;
  /** Authoritative tenant scope, ANDed on top of `filters`. */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  timeout?: 'extended';
}

/**
 * A page of mappings.
 *
 * `facet_filtered` is present and true only when a `used_by_*` filter is
 * active. The facets are derived per row and cannot be expressed in SQL, so
 * the server narrows the page in Python and `count` then describes the
 * pre-facet superset. The grid must surface that rather than show a total it
 * cannot honour.
 */
export interface ExtIdPage extends PaginatedResponse<ExtIdListItem> {
  facet_filtered?: boolean;
  facet_dropped_on_page?: number;
}

function toQuery(p: ExtIdListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(p: ExtIdListParams = {}): Promise<ExtIdPage> {
  const qs = toQuery(p);
  return apiGet(`/ext_ids/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** `id` is the composite row id, e.g. `aeid:1234`. */
export async function retrieve(id: string): Promise<ExtIdDetail> {
  return apiGet(`/ext_ids/${encodeURIComponent(id)}/`);
}


export interface ExtIdCreate {
  tour_operator_id: number;
  /** Creation always makes an alias, so this is the tour it points at. */
  tour_id: number;
  custom_id: string;
  id_type?: ExtIdType;
  use_for_matching?: boolean;
  api_queryable?: boolean;
  integration?: string | null;
  restech_account?: string | null;
  comment?: string | null;
  ext_link?: string | null;
  tag_ids?: number[];
  target_ids?: number[];
}

/** Every field an edit may carry. A primary row accepts `custom_id` only; the
 *  server rejects the rest rather than silently dropping them. A managed alias
 *  accepts nothing but `managed` — unless the same body takes it over
 *  (`managed: false`); anything else answers 400 `{managed: "..."}`. */
export interface ExtIdUpdate {
  /** false = take the alias over from its import; true = hand it back. */
  managed?: boolean;
  custom_id?: string;
  id_type?: ExtIdType;
  use_for_matching?: boolean;
  api_queryable?: boolean;
  integration?: string | null;
  restech_account?: string | null;
  comment?: string | null;
  ext_link?: string | null;
  /** Retarget the mapping onto a different tour. */
  tour_id?: number;
  /** Replaces the whole set. */
  tag_ids?: number[];
  /** Replaces the whole set. Each target ALSO receives a booking on match. */
  target_ids?: number[];
}

export async function create(body: ExtIdCreate): Promise<ExtIdDetail> {
  return apiPost(`/ext_ids/`, body);
}

export async function update(id: string, body: ExtIdUpdate): Promise<ExtIdDetail> {
  return apiPatch(`/ext_ids/${encodeURIComponent(id)}/`, body);
}

/** Aliases only — a primary mapping 400s with an explanation, and so does a
 *  managed alias (`{managed: "..."}`: take it over first). */
export async function remove(id: string): Promise<void> {
  return apiDelete(`/ext_ids/${encodeURIComponent(id)}/`);
}
